import { A } from "@solidjs/router";
import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";

import type { HeatmapFirm, HeatmapSector } from "../api";

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SectorBox extends Box {
  data: HeatmapSector;
}

interface FirmBox extends Box {
  data: HeatmapFirm;
}

interface SquarifyItem<T> {
  value: number;
  data: T;
}

function worst<T>(row: SquarifyItem<T>[], sum: number, totalValue: number, area: number, shortest: number): number {
  if (row.length === 0 || sum <= 0 || shortest <= 0) return Infinity;
  const rowArea = (sum / totalValue) * area;
  const rowLength = rowArea / shortest;
  if (rowLength <= 0) return Infinity;
  let worstRatio = 0;
  for (const item of row) {
    const itemArea = (item.value / sum) * rowArea;
    const itemSide = itemArea / rowLength;
    if (itemSide <= 0) return Infinity;
    const ratio = Math.max(rowLength / itemSide, itemSide / rowLength);
    if (ratio > worstRatio) worstRatio = ratio;
  }
  return worstRatio;
}

function squarify<T>(items: SquarifyItem<T>[], box: Box): Array<Box & { data: T }> {
  const sorted = items
    .filter((i) => i.value > 0)
    .slice()
    .sort((a, b) => b.value - a.value);
  const out: Array<Box & { data: T }> = [];
  layoutSquarified(sorted, box, out);
  return out;
}

function layoutSquarified<T>(items: SquarifyItem<T>[], box: Box, out: Array<Box & { data: T }>): void {
  if (items.length === 0 || box.w <= 0 || box.h <= 0) return;
  if (items.length === 1) {
    out.push({ ...box, data: items[0].data });
    return;
  }

  const totalValue = items.reduce((s, it) => s + it.value, 0);
  if (totalValue <= 0) return;
  const area = box.w * box.h;
  const shortest = Math.min(box.w, box.h);
  const horizontal = box.w >= box.h;

  let rowEnd = 1;
  let rowSum = items[0].value;
  let currentWorst = worst(items.slice(0, 1), rowSum, totalValue, area, shortest);

  while (rowEnd < items.length) {
    const tryEnd = rowEnd + 1;
    const trySum = rowSum + items[rowEnd].value;
    const tryWorst = worst(items.slice(0, tryEnd), trySum, totalValue, area, shortest);
    if (tryWorst > currentWorst) break;
    rowSum = trySum;
    currentWorst = tryWorst;
    rowEnd = tryEnd;
  }

  const row = items.slice(0, rowEnd);
  const rowArea = (rowSum / totalValue) * area;
  const rowLength = horizontal ? rowArea / box.h : rowArea / box.w;

  let cursor = horizontal ? box.y : box.x;
  for (const item of row) {
    const itemArea = (item.value / rowSum) * rowArea;
    if (horizontal) {
      const itemH = itemArea / rowLength;
      out.push({ x: box.x, y: cursor, w: rowLength, h: itemH, data: item.data });
      cursor += itemH;
    } else {
      const itemW = itemArea / rowLength;
      out.push({ x: cursor, y: box.y, w: itemW, h: rowLength, data: item.data });
      cursor += itemW;
    }
  }

  const remaining = items.slice(rowEnd);
  const newBox: Box = horizontal
    ? { x: box.x + rowLength, y: box.y, w: box.w - rowLength, h: box.h }
    : { x: box.x, y: box.y + rowLength, w: box.w, h: box.h - rowLength };
  layoutSquarified(remaining, newBox, out);
}

function marginColor(margin: number | null, theme: "light" | "dark"): string {
  if (margin == null || !isFinite(margin)) {
    return theme === "light" ? "hsl(40 8% 82%)" : "hsl(220 6% 22%)";
  }
  const clamped = Math.max(-0.3, Math.min(0.3, margin));
  const t = Math.abs(clamped) / 0.3; // 0..1
  if (theme === "light") {
    // Cream-friendly: positive sage green, negative warm salmon.
    const hue = clamped >= 0 ? 138 : 8;
    const sat = 22 + t * 36; // 22..58
    const light = 84 - t * 26; // 84..58
    return `hsl(${hue} ${sat}% ${light}%)`;
  }
  // Dark mode: deeper saturated cells on near-black background.
  const hue = clamped >= 0 ? 140 : 358;
  const sat = 12 + t * 48;
  const light = 30 - t * 8;
  return `hsl(${hue} ${sat}% ${light}%)`;
}

function readThemeAttr(): "light" | "dark" {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light"
    ? "light"
    : "dark";
}

function fmtMoneyB(b: number): string {
  if (b >= 1000) return `$${(b / 1000).toFixed(2)}T`;
  if (b >= 10) return `$${b.toFixed(0)}B`;
  return `$${b.toFixed(1)}B`;
}

function fmtMargin(m: number | null): string {
  if (m == null) return "n/a";
  return `${(m * 100).toFixed(1)}%`;
}

const SECTOR_HEADER = 27;
const SECTOR_PAD = 1;

export default function Heatmap(props: { data: HeatmapSector[] }) {
  let containerRef: HTMLDivElement | undefined;
  const [width, setWidth] = createSignal(0);
  const [height, setHeight] = createSignal(0);
  const [theme, setTheme] = createSignal<"light" | "dark">(readThemeAttr());

  onMount(() => {
    if (!containerRef) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setWidth(e.contentRect.width);
        setHeight(e.contentRect.height);
      }
    });
    ro.observe(containerRef);

    const mo = new MutationObserver(() => setTheme(readThemeAttr()));
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    onCleanup(() => {
      ro.disconnect();
      mo.disconnect();
    });
  });

  const layout = createMemo(() => {
    const w = width();
    const h = height();
    if (w <= 0 || h <= 0) return { sectors: [] as Array<SectorBox & { firms: FirmBox[] }> };

    const sectorItems: SquarifyItem<HeatmapSector>[] = props.data.map((s) => ({
      value: s.revenue_b_usd,
      data: s,
    }));
    const sectorBoxes = squarify(sectorItems, { x: 0, y: 0, w, h });

    const result = sectorBoxes.map((sb) => {
      const inner: Box = {
        x: sb.x + SECTOR_PAD,
        y: sb.y + SECTOR_HEADER,
        w: Math.max(0, sb.w - SECTOR_PAD * 2),
        h: Math.max(0, sb.h - SECTOR_HEADER - SECTOR_PAD),
      };
      const firmItems: SquarifyItem<HeatmapFirm>[] = sb.data.firms.map((f) => ({
        value: f.revenue_b_usd,
        data: f,
      }));
      const firms = squarify(firmItems, inner);
      return { ...sb, firms };
    });

    return { sectors: result };
  });

  return (
    <div class="heatmap" ref={(el) => (containerRef = el)}>
      <Show when={width() > 0 && height() > 0}>
        <For each={layout().sectors}>
          {(sec) => (
            <>
              <div
                class="hm-sector-label"
                style={{
                  left: `${sec.x + SECTOR_PAD}px`,
                  top: `${sec.y}px`,
                  width: `${Math.max(0, sec.w - SECTOR_PAD * 2)}px`,
                  height: `${SECTOR_HEADER}px`,
                }}
              >
                <span class="hm-sec-name">{sec.data.name}</span>
                <span class="hm-sec-rev">{fmtMoneyB(sec.data.revenue_b_usd)} rev</span>
              </div>
              <For each={sec.firms}>
                {(cell) => {
                  const showLabel = cell.w > 32 && cell.h > 22;
                  const bigLabel = cell.w > 62 && cell.h > 46;
                  return (
                    <A
                      class="hm-cell"
                      href={`/firm/${cell.data.slug}`}
                      title={`${cell.data.name}${cell.data.industry ? " · " + cell.data.industry : ""} · rev ${fmtMoneyB(cell.data.revenue_b_usd)} · mcap ${fmtMoneyB(cell.data.market_cap_b_usd)} · op margin ${fmtMargin(cell.data.operating_margin)}`}
                      style={{
                        left: `${cell.x}px`,
                        top: `${cell.y}px`,
                        width: `${Math.max(0, cell.w - 1)}px`,
                        height: `${Math.max(0, cell.h - 1)}px`,
                        "background-color": marginColor(cell.data.operating_margin, theme()),
                      }}
                    >
                      <Show when={showLabel}>
                        <span class={`hm-tick ${bigLabel ? "big" : ""}`}>
                          {cell.data.ticker ?? cell.data.slug.toUpperCase()}
                        </span>
                        <Show when={bigLabel}>
                          <span class="hm-margin">{fmtMargin(cell.data.operating_margin)}</span>
                        </Show>
                      </Show>
                    </A>
                  );
                }}
              </For>
            </>
          )}
        </For>
      </Show>
    </div>
  );
}
